import { Injectable, inject } from '@angular/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DiscardCaseDialogComponent } from 'src/app/dialogs/case/discard-case-dialog/discard-case-dialog.component';
import { ClientsService } from '../clients/clients.service';

type CreateCaseParams = {
  product_type: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
};

type CreateCaseResponse = {
  id: string;
};

export type PaymentStatus =
  'Prepared'
  | 'Started'
  | 'InProgress'
  | 'Waiting'
  | 'Reserved'
  | 'Authorized'
  | 'Canceled'
  | 'Succeeded'
  | 'Failed'
  | 'PartiallySucceeded'
  | 'Expired';

export type GetCardPaymentStatus = {
  total: number;
  currency_iso: string;
  status: PaymentStatus;
};

type GetCaseByTrackingIdResponse = {
  payee_case_id: string;
  payee_case_reference_id: string;
};

type StartCaseResponse = {
  id: string;
  payee_case_reference_id: string;
};

@Injectable({
  providedIn: 'root'
})
export class CasesService {
  private clientsService = inject(ClientsService);
  private dialog = inject(MatDialog);


  async getCaseByClaimToken(trackingId: string): Promise<GetCaseByTrackingIdResponse> {
    const url = `${environment.baseUrl}/api/claim-by-tracking-id/${trackingId}`;
    const result = await axios.get<GetCaseByTrackingIdResponse>(url);
    return result.data;
  }

  async createCase(params: CreateCaseParams, clientId = this.clientsService.selectedClientId): Promise<CreateCaseResponse> {
    if (!clientId) {
      throw new Error('No client id');
    }

    const url = environment.baseUrl + `/api/client/${clientId}/case`;
    const result = await axios.post<CreateCaseResponse>(url, params);
    return result.data;
  }

  openDeleteCaseDialog(id: string): MatDialogRef<DiscardCaseDialogComponent> {
    return this.dialog.open(DiscardCaseDialogComponent, {
      data: {
        id,
      },
    });
  }

  async deleteCase(caseId: string, clientId = this.clientsService.selectedClientId): Promise<void> {
    if (!clientId || !caseId) {
      return;
    }

    const url = environment.baseUrl + `/api/payee-case/${caseId}`;
    await axios.delete(url);
  }

  async getCaseForm(caseId: string, step: 'init' | 'summary' | 'payment'): Promise<unknown> {
    if (!caseId) {
      throw new Error('Case id missing in getCaseForm');
    }
    const url = `${environment.baseUrl}/api/payee-case/${caseId}/${step}`;
    const response = await axios.get(url);
    return response.data;
  }

  async getCardPaymentStatus(paymentId: string, caseId: string): Promise<GetCardPaymentStatus> {
    if (!caseId || !paymentId) {
      throw new Error('getCardPaymentStatus - Some parameters are missing');
    }

    const url = environment.baseUrl + `/api/case/${caseId}/get-card-payment-status/${paymentId}`;
    const response = await axios.get<GetCardPaymentStatus>(url);
    return response.data;
  }

  async startWithCardPayment(caseId: string): Promise<void> {
    if (!caseId) {
      return;
    }

    const url = environment.baseUrl + `/api/case/${caseId}/start-with-card-payment`;
    const result = await axios.get<string>(url);
    window.location.href = result.data;
  }

  async resendProForma(caseId: string): Promise<void> {
    const url = environment.baseUrl + `/api/payee-case/${caseId}/resend-pro-forma`;
    await axios.post(url);
  }

  async startWithTransfer(caseId: string): Promise<void> {
    if (!caseId) {
      return;
    }

    const url = environment.baseUrl + `/api/case/${caseId}/start-with-transfer`;
    await axios.post<string>(url);
  }

  async startWithUnknownFees(caseId: string, clientId = this.clientsService.selectedClientId): Promise<void> {
    if (!clientId || !caseId) {
      return;
    }

    const url = environment.baseUrl + `/api/case-form/${caseId}/payment/start-with-unknown-fees`;
    await axios.post<StartCaseResponse>(url);
  }

  async changeProduct(caseId: string, productId: number): Promise<void> {
    if (!caseId || !productId) {
      return;
    }

    const url = environment.baseUrl + `/api/payee-case/${caseId}/change-product/${productId}`;
    await axios.post(url, {
      '_method': 'PUT',
    });
  }
}
